// Administration → Asistent AI: Gemini settings and AI-assisted creation of a document type.
import React, { useEffect, useState } from 'react';
import { api } from '../../api';
import { useCatalog } from '../../catalog';
import { navigate } from '../../store';
import type { Attribute, AttrType, ValueList } from '../../types';
import { Field, Switch, useToast } from '../../ui';
import { Icon } from '../../ui/Icon';
import { typeLabel } from './common';

interface AiSettings { configured: boolean; apiKeyMasked?: string | null; model?: string | null }
interface PropList { existingListId?: number; code?: string; name?: string; allowAdd?: boolean; items?: any[] }
interface PropAttr { existingAttributeId?: number; code?: string; name?: string; type?: AttrType; required?: boolean; showInList?: boolean; section?: string; description?: string; options?: any; list?: PropList }
interface Proposal { name: string; code: string; description?: string; icon?: string; color?: string; isRegistry?: boolean; registryPrefix?: string; seqResetYearly?: boolean; attributes: PropAttr[] }

export function AiPage() {
  return (
    <div className="stack" style={{ gap: 18, maxWidth: 980 }}>
      <SettingsCard />
      <SuggestCard />
    </div>
  );
}

function SettingsCard() {
  const toast = useToast();
  const [s, setS] = useState<AiSettings | null>(null);
  const [apiKey, setApiKey] = useState('');
  const [model, setModel] = useState('gemini-2.5-flash');
  const [busy, setBusy] = useState<'save' | 'test' | null>(null);

  const load = () => api.get<AiSettings>('/api/ai/settings').then(x => { setS(x); if (x.model) setModel(x.model); }).catch(e => toast(e.message, 'error'));
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy('save');
    try {
      const body: any = { model: model.trim() || 'gemini-2.5-flash' };
      if (apiKey.trim()) body.apiKey = apiKey.trim();
      await api.put('/api/ai/settings', body);
      toast('Setări salvate', 'ok'); setApiKey(''); load();
    } catch (e: any) { toast(e.message, 'error'); } finally { setBusy(null); }
  };
  const test = async () => {
    setBusy('test');
    try { const r = await api.post<{ ok: boolean; reply: string; model: string }>('/api/ai/test'); toast(r.ok ? `Conexiune reușită (${r.model}): ${r.reply}` : 'Testul a eșuat', r.ok ? 'ok' : 'error'); }
    catch (e: any) { toast(e.message, 'error'); } finally { setBusy(null); }
  };

  return (
    <div className="card">
      <div className="card-head"><Icon name="key" /><h2>Conexiune Gemini</h2><span className="grow" />{s && <span className={'tag ' + (s.configured ? 'adm-tag-ok' : '')}>{s.configured ? 'configurat' : 'neconfigurat'}</span>}</div>
      <div className="card-body stack" style={{ gap: 14 }}>
        <div className="banner"><Icon name="info" /><span>Cheia API este păstrată doar pe server și este folosită exclusiv pentru asistență la configurare (propuneri de tipuri de documente). Conținutul documentelor nu este trimis către Google. Obțineți o cheie gratuită de la <a href="https://aistudio.google.com/apikey" target="_blank" rel="noopener">aistudio.google.com/apikey</a>.</span></div>
        <div className="form-grid">
          <Field label="Cheie API" hint={s?.configured ? 'Lăsați gol pentru a păstra cheia actuală' : undefined}>
            <input className="input" type="password" autoComplete="off" value={apiKey} onChange={e => setApiKey(e.target.value)} placeholder={s?.configured ? (s.apiKeyMasked || '••••••••') : 'AIza…'} />
          </Field>
          <Field label="Model" hint="Implicit gemini-2.5-flash"><input className="input" value={model} onChange={e => setModel(e.target.value)} placeholder="gemini-2.5-flash" /></Field>
        </div>
        <div className="row">
          <button className="btn primary" disabled={busy != null} onClick={save}>{busy === 'save' ? 'Se salvează…' : 'Salvează'}</button>
          <button className="btn" disabled={busy != null || !(s?.configured || apiKey)} onClick={test}><Icon name="sparkles" />{busy === 'test' ? 'Se testează…' : 'Testează conexiunea'}</button>
        </div>
      </div>
    </div>
  );
}

const EXAMPLE = 'Registru de contracte: număr contract, partener, valoare, dată semnare, dată expirare, responsabil, status';

function SuggestCard() {
  const toast = useToast();
  const { reload } = useCatalog();
  const [desc, setDesc] = useState('');
  const [busy, setBusy] = useState<'suggest' | 'apply' | null>(null);
  const [p, setP] = useState<Proposal | null>(null);
  const [attrs, setAttrs] = useState<Attribute[]>([]);
  const [lists, setLists] = useState<ValueList[]>([]);
  const [result, setResult] = useState<{ type: any; log: string[] } | null>(null);

  const suggest = async () => {
    if (!desc.trim()) return;
    setBusy('suggest'); setResult(null);
    try {
      const [prop, a, l] = await Promise.all([api.post<Proposal>('/api/ai/suggest-type', { description: desc.trim() }), api.get<Attribute[]>('/api/catalog/attributes'), api.get<ValueList[]>('/api/catalog/lists')]);
      setAttrs(a); setLists(l); setP({ ...prop, attributes: prop.attributes || [] });
    } catch (e: any) { toast(e.message, 'error'); } finally { setBusy(null); }
  };
  const apply = async () => {
    if (!p) return;
    setBusy('apply');
    try {
      const r = await api.post<{ type: any; log: string[] }>('/api/ai/apply', { proposal: p });
      setResult(r); setP(null); toast(`Tipul „${r.type?.name || p.name}” a fost creat`, 'ok'); reload();
    } catch (e: any) { toast(e.message, 'error'); } finally { setBusy(null); }
  };
  const setProp = (x: Partial<Proposal>) => setP(v => v ? { ...v, ...x } : v);
  const updAttr = (i: number, x: Partial<PropAttr>) => setProp({ attributes: p!.attributes.map((a, j) => j === i ? { ...a, ...x } : a) });

  return (
    <div className="card">
      <div className="card-head"><Icon name="sparkles" /><h2>Configurează un tip de document cu AI</h2></div>
      <div className="card-body stack" style={{ gap: 14 }}>
        <p className="muted small">Descrieți în cuvinte ce fel de documente vreți să gestionați și ce informații trebuie reținute. Asistentul propune un tip de document cu atributele și listele de valori necesare; le puteți ajusta înainte de creare.</p>
        <textarea className="textarea" style={{ minHeight: 90 }} value={desc} onChange={e => setDesc(e.target.value)} placeholder={EXAMPLE} />
        <div className="row">
          <button className="btn primary" disabled={busy != null || !desc.trim()} onClick={suggest}><Icon name="sparkles" />{busy === 'suggest' ? 'Se generează…' : 'Propune configurația'}</button>
          {!desc && <button className="btn ghost sm" onClick={() => setDesc(EXAMPLE)}>Folosește exemplul</button>}
        </div>

        {p && (
          <div className="adm-proposal">
            <div className="section-title">Propunere</div>
            <div className="form-grid">
              <Field label="Nume"><input className="input" value={p.name} onChange={e => setProp({ name: e.target.value })} /></Field>
              <Field label="Cod"><input className="input" value={p.code} onChange={e => setProp({ code: e.target.value })} /></Field>
              <Field label="Descriere" className="span2"><input className="input" value={p.description || ''} onChange={e => setProp({ description: e.target.value })} /></Field>
              <Field label="Aspect"><div className="row"><span className="type-tile" style={{ padding: 6, cursor: 'default' }}><span className="ico" style={{ background: p.color || '#2d5fb8', width: 30, height: 30 }}><Icon name={p.icon || 'file'} size={16} /></span></span><span className="code">{p.icon || 'file'}</span><span className="code">{p.color || '—'}</span></div></Field>
              <Field label="Registru">
                <div className="stack" style={{ gap: 6 }}>
                  <Switch on={!!p.isRegistry} onChange={v => setProp({ isRegistry: v })} label="Este registru" />
                  {p.isRegistry && <div className="row"><input className="input" style={{ width: 120 }} value={p.registryPrefix || ''} onChange={e => setProp({ registryPrefix: e.target.value })} placeholder="Prefix" /><Switch on={p.seqResetYearly !== false} onChange={v => setProp({ seqResetYearly: v })} label="Resetare anuală" /></div>}
                </div>
              </Field>
            </div>
            <div className="section-title">Atribute ({p.attributes.length})</div>
            <div className="stack" style={{ gap: 6 }}>
              {p.attributes.map((a, i) => {
                const ex = a.existingAttributeId ? attrs.find(x => x.id === a.existingAttributeId) : null;
                const exList = a.list?.existingListId ? lists.find(l => l.id === a.list!.existingListId) : null;
                return (
                  <div key={i} className="adm-prop-attr">
                    <div className="row" style={{ flexWrap: 'wrap' }}>
                      {ex ? <span className="row" style={{ gap: 6 }}><span className="tag adm-tag-accent">reutilizează</span><b style={{ fontWeight: 500 }}>{ex.name}</b><span className="tag">{typeLabel(ex.type)}</span>{ex.listName && <span className="muted small">lista „{ex.listName}”</span>}</span>
                        : <><input className="input" style={{ width: 220 }} value={a.name || ''} onChange={e => updAttr(i, { name: e.target.value })} placeholder="Nume atribut" /><span className="tag">{typeLabel(a.type || 'TEXT')}</span></>}
                      <input className="input" style={{ width: 160 }} value={a.section || ''} onChange={e => updAttr(i, { section: e.target.value })} placeholder="Secțiune" />
                      <Switch on={!!a.required} onChange={v => updAttr(i, { required: v })} label={<span className="small">obligatoriu</span>} />
                      <Switch on={a.showInList !== false} onChange={v => updAttr(i, { showInList: v })} label={<span className="small">în listă</span>} />
                      <span className="grow" />
                      <button className="icon-btn" title="Elimină" onClick={() => setProp({ attributes: p.attributes.filter((_, j) => j !== i) })}><Icon name="x" /></button>
                    </div>
                    {!ex && a.description && <div className="muted small" style={{ marginTop: 4 }}>{a.description}</div>}
                    {!ex && a.type === 'LIST' && a.list && (
                      <div className="row small" style={{ marginTop: 6, flexWrap: 'wrap' }}>
                        <Icon name="list" size={14} />
                        {a.list.existingListId ? <span>listă existentă: <b style={{ fontWeight: 500 }}>{exList?.name || `#${a.list.existingListId}`}</b></span> : (
                          <><span>listă nouă <b style={{ fontWeight: 500 }}>{a.list.name || a.list.code}</b>{a.list.allowAdd && <span className="muted"> (utilizatorii pot adăuga valori)</span>}:</span>
                            <span className="pill-list">{(a.list.items || []).map((it: any, k: number) => <span key={k} className="tag">{typeof it === 'string' ? it : (it.label || it.value)}</span>)}</span></>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
              {p.attributes.length === 0 && <p className="muted small">Niciun atribut; tipul va fi creat fără câmpuri.</p>}
            </div>
            <div className="row mt">
              <button className="btn primary" disabled={busy != null || !p.name.trim()} onClick={apply}><Icon name="check" />{busy === 'apply' ? 'Se creează…' : 'Creează tipul de document'}</button>
              <button className="btn" disabled={busy != null} onClick={() => setP(null)}>Renunță</button>
            </div>
          </div>
        )}

        {result && (
          <div className="adm-proposal">
            <div className="section-title">Rezultat</div>
            <ul className="adm-log">{(result.log || []).map((l, i) => <li key={i}>{l}</li>)}</ul>
            <div className="row mt">
              <button className="btn primary" onClick={() => navigate(result.type?.id ? `/admin/types?open=${result.type.id}` : '/admin/types')}><Icon name="arrowRight" />Deschide tipul în „Tipuri de documente”</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
